import { Button as NextButton, ButtonProps as NextButtonProps, Loading } from '@nextui-org/react';
import React from 'react';
import styled from 'styled-components';
import { StyledComponent } from 'styled-components';

import ZIndexLayers from '../../services/ZIndexLayers';
import { ThemeProp } from '../../theme/Themes';
import { lightTheme } from '../../theme/Themes';

enum ButtonRoleEnum {
  Cancel,
  Destructive,
  Primary,
}

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export class ButtonRole {
  static Cancel: ButtonRole = new ButtonRole(ButtonRoleEnum.Cancel);
  static Destructive: ButtonRole = new ButtonRole(ButtonRoleEnum.Destructive);
  static Primary: ButtonRole = new ButtonRole(ButtonRoleEnum.Primary);

  constructor(private buttonRole: ButtonRoleEnum) {}

  backgroundColor(theme = lightTheme): string {
    switch (this.buttonRole) {
      case ButtonRoleEnum.Destructive:
        return theme.colors.darkRed;
      default:
        return theme.colors.mainColor;
    }
  }
}

interface ButtonProps extends NextButtonProps {
  loading?: boolean;
  loadingSize?: ButtonSize;
  $disableMinWidth?: boolean;
  flex?: string;
}

export const StyledLoading = styled(Loading)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: ${ZIndexLayers.overlay};
`;

const calculateFontSize = (size: ButtonSize): string => {
  switch (size) {
    case 'xs':
      return '0.75rem';
    case 'sm':
    case 'md':
      return '0.875rem';
    case 'lg':
      return '1rem';
    case 'xl':
      return '1.25rem';
  }
};

const calculateButtonHeight = (size: ButtonSize): string => {
  switch (size) {
    case 'xs':
      return '1.5rem';
    case 'sm':
      return '2rem';
    case 'md':
      return '2.5rem';
    case 'lg':
      return '3rem';
    case 'xl':
      return '3.5rem';
  }
};

const Button = styled(
  React.forwardRef<HTMLElement>(
    ({ children, loading, loadingSize = 'md', rounded = true, ...props }: ButtonProps, ref) => {
      return (
        <NextButton {...props} rounded={rounded} ref={ref}>
          {/* we should wrap to div if children item is a text to make css conditions work */}
          {typeof children === 'string' ? <div>{children}</div> : children}
          {loading && <StyledLoading size={loadingSize} />}
        </NextButton>
      );
    },
  ),
).attrs((props: ButtonProps & ThemeProp) => ({
  ...props,
  css: {
    position: 'relative',
    'pointer-events': props.loading ? 'none' : 'auto',
    'font-weight': '$normal',
    'font-size': calculateFontSize(props.size as ButtonSize),
    'min-width': props.$disableMinWidth ? '0' : '$25',
    padding: '$6a $8',
    height: calculateButtonHeight(props.size as ButtonSize),
    color: props.color || props.theme.colors.content.white,
    gap: '0.5rem',
    background: props.theme.gradients.button.default,

    '&:focus': {
      background: props.theme.gradients.button.pressed,
    },
    '&:hover': {
      background: props.theme.gradients.button.hover,
    },
    '&:disabled': {
      background: props.theme.colors.neutrals.c200,
      color: props.theme.colors.content.disabled,
    },
    ...props.css,
  },
}))`
  ${(props) => props.flex && `flex: ${props.flex};`}
  .nextui-loading {
    --nextui--loadingColor: ${(props: ButtonProps & ThemeProp) =>
      props.color || props.theme.colors.content.white} !important;
  }
  .nextui-button-icon {
    margin-right: 0;
    z-index: ${ZIndexLayers.default};
  }
  .nextui-button-text {
    z-index: ${ZIndexLayers.default};
    display: ${(props: ButtonProps) => (props.children ? 'block' : 'none')};
    /* We set visibility to hidden to all children except loader if loading is true */
    > * {
      :not(${StyledLoading}) {
        visibility: ${(props: ButtonProps & ThemeProp) => (props.loading ? 'hidden' : 'visible')};
      }
    }
  }
` as StyledComponent<({ children, loading, loadingSize, ...props }: ButtonProps) => JSX.Element, {}>; // eslint-disable-line @typescript-eslint/ban-types

export default Button;
